import { useContext, useMemo } from 'react';
import { z } from 'zod';
import { useFormContext, SubmitHandler, useWatch } from 'react-hook-form';
import { Button } from '@ds';
import { MdOutlineArrowRightAlt } from 'react-icons/md';
import { AuthContext } from '@lp/core/auth';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { EUROPE_UNOS_ID_REGEX, UNOS_ID_REGEX } from '@/constants/case/case.constants';
import { EUROPE_REGION_ID } from '@/constants/serviceRegion.constants';
import { CaseBoardPermissions } from '@/permissions';
import { OrganType, ServiceType } from '@/api/apis/case';

type FormActionsProps = {
  handleSubmitDraft: SubmitHandler<CaseFormType>;
  handleClose: () => void;
  isLoading?: boolean;
  canCreateKidneyCase?: boolean;
  isEditMode?: boolean;
  originalOrganType?: OrganType;
};

export const FormActions = ({
  handleClose,
  handleSubmitDraft,
  isLoading,
  canCreateKidneyCase = true,
  isEditMode = false,
  originalOrganType,
}: FormActionsProps) => {
  const {
    getFieldState,
    formState: { isValid, isSubmitting },
    getValues,
    control,
  } = useFormContext<CaseFormType>();

  const [donorId, serviceRegionId, serviceType, donorHospitalDate, organType] = useWatch<CaseFormType>({
    control,
    name: ['donorId', 'serviceRegionId', 'serviceType', 'donorHospitalDate', 'organType'],
  });

  const { checkPermission } = useContext(AuthContext);

  const canChangeDraftCases = checkPermission(CaseBoardPermissions.CaseDraftUpdate);

  const handleDraft = () => {
    handleSubmitDraft(getValues());
  };

  const hasMinimalRequiredValue = useMemo(() => {
    const schema = z.string().regex(serviceRegionId === EUROPE_REGION_ID ? EUROPE_UNOS_ID_REGEX : UNOS_ID_REGEX);
    const fieldState = getFieldState('donorId');

    if (fieldState.isDirty || donorId) {
      const validated = schema.safeParse(donorId);
      return validated.success;
    }

    return false;
  }, [getFieldState, donorId, serviceRegionId]);

  const isDaCase = serviceType === ServiceType.DirectAcquisition;
  const isKidneyBlocked =
    !canCreateKidneyCase && organType === OrganType.Kidney && !(isEditMode && originalOrganType === OrganType.Kidney);

  const canProceedToClinicalScreening =
    isValid && (!isDaCase || !!donorHospitalDate) && canChangeDraftCases && !isKidneyBlocked;

  return (
    <>
      <Button variant="light" size="md" onClick={handleClose}>
        Discard Case
      </Button>
      <div className="flex gap-4">
        {canChangeDraftCases && (
          <Button
            variant="outline"
            size="md"
            isDisabled={!hasMinimalRequiredValue || isKidneyBlocked}
            isLoading={isSubmitting || isLoading}
            onClick={handleDraft}
          >
            Save Draft
          </Button>
        )}
        {canChangeDraftCases && (
          <Button
            type="submit"
            variant="solid"
            size="md"
            isDisabled={!canProceedToClinicalScreening}
            isLoading={isSubmitting || isLoading}
            endContent={<MdOutlineArrowRightAlt size={18} />}
          >
            Proceed to Clinical Screening
          </Button>
        )}
      </div>
    </>
  );
};
